import React, { useState, useEffect } from "react";
import { Modal, Switch, styled } from "@mui/material";
import { Close } from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";

import Styles from "./RaDocumentation.module.css";
import { formatDateElement } from "../../../../commons/FormatDate";
import CircularProgress from "../../../ReverseAuction/Bidding/CircularProgress";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../../commons/Toast/ErrorMessage";
import {
  approveQuantityRequest,
  updateQuantityRequest,
  updateWinnerBidStatus,
} from "../../../../Services/ReverseAuction/AlterApi";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import { usePdfPreview } from "../../../../hooks/usePdfPreview";
import { getequisitionsList } from "../../../../Services/ReverseAuction/GetApi";
import { useSelector } from "react-redux";

// --- Styled Components ---
const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": { width: 15 },
    "& .MuiSwitch-switchBase.Mui-checked": { transform: "translateX(9px)" },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "var(--primary-color-text)",
        ...theme.applyStyles("dark", {
          backgroundColor: "var(--primary-color-text)",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], { duration: 200 }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(149, 148, 148, 0.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", { backgroundColor: "rgba(255,255,255,.35)" }),
  },
}));

// --- Helper: Status Text Formatter ---
const formatStatusText = (status) => {
  if (!status) return "-";
  // Capitalize first letter, lowercase the rest (e.g., "ACCEPTED" -> "Accepted")
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

// --- Sub-Component: BidRow ---
const BidRow = ({
  bid,
  tenderStatus,
  editingState,
  setEditingState,
  onUpdateQuantity,
  onToggleStatus,
  minimumQty,
}) => {
  const {
    id: bidId,
    winnerBidStatus,
    selectedQuantity,
    bidQuantityMw,
    bidPrice,
    requestedQuantity,
  } = bid;
  const { editId, tempQuantity } = editingState;

  const isBucketFilled =
    tenderStatus === "BUCKET_FILLED_RA" || tenderStatus === "BUCKET_FILLED";

  const isEditable =
    ["ACCEPTED", "ABSCOND"].includes(winnerBidStatus) && isBucketFilled;
  const isPending = winnerBidStatus === "PENDING";
  const isEditingThis = editId === bidId;

  const handleEditClick = () => {
    setEditingState({ editId: bidId, tempQuantity: selectedQuantity || 0 });
  };

  const handleCancelClick = () => {
    setEditingState({ editId: "", tempQuantity: "" });
  };

  const handleSaveClick = () => {
    // if (Number(tempQuantity) < Number(minimumQty)) {
    //   ErrorMessage(`Minimum quantity to allocate is ${minimumQty} MW`);
    //   return;
    // }
    onUpdateQuantity(bidId, tempQuantity, bidQuantityMw, minimumQty);
  };

  const { handlePreviewGeneralTerms, PdfPreviewModal } = usePdfPreview();

  return (
    <>
      <PdfPreviewModal />
      <div
        className={`${Styles.bidViewContainer} display_flex_row tableDetails`}
      >
        <div style={{ width: "10%" }}>{bidPrice}</div>
        <div style={{ width: "10%" }}>{bidQuantityMw}</div>
        <div
          style={{
            width: "20%",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isEditable ? (
            isEditingThis ? (
              <>
                <div className={Styles.sendReqButton} onClick={handleSaveClick}>
                  <DoneIcon
                    style={{
                      fontSize: "16px",
                      color: "#f1935c",
                      cursor: "pointer",
                    }}
                  />
                </div>
                <div
                  className={Styles.removeElement}
                  onClick={handleCancelClick}
                >
                  <Close style={{ fontSize: "16px", cursor: "pointer" }} />
                </div>
              </>
            ) : (
              <>
                <div className={Styles.removeElement} onClick={handleEditClick}>
                  <EditIcon
                    style={{
                      fontSize: "16px",
                      color: "#6f6d7c",
                      cursor: "pointer",
                    }}
                  />
                </div>
                <div>
                  <AntSwitch
                    checked={["ACCEPTED", "PENDING"].includes(winnerBidStatus)}
                    onChange={() =>
                      onToggleStatus(
                        bidId,
                        winnerBidStatus === "ACCEPTED" ? "ABSCOND" : "ACCEPTED",
                      )
                    }
                  />
                </div>
              </>
            )
          ) : isPending ? (
            <div className={"pending"}>NA</div>
          ) : (
            "-"
          )}
        </div>
        <div style={{ width: "20%" }}>
          {isEditingThis ? (
            <div className={Styles.inputContainermain}>
              <input
                type="number"
                className={Styles.qtyInput}
                value={tempQuantity}
                onChange={(e) => {
                  if(Number(e.target.value) > Number(bidQuantityMw)) {
                    ErrorMessage(`Cannot allocate more than bid quantity of ${bidQuantityMw} MW`);
                    return;
                  }
                  setEditingState((prev) => ({
                    ...prev,
                    tempQuantity: e.target.value,
                  }));
                }}
                style={{
                  width: "80%",
                  padding: "4px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>
          ) : (
            <>
              {winnerBidStatus === "ABSCOND" ? (
                0
              ) : (
                <>{requestedQuantity || selectedQuantity || "-"}</>
              )}
            </>
          )}
        </div>
        <div style={{ width: "10%" }}>
          <RemoveRedEyeIcon
            style={{
              fontSize: "12px",
              color: "#f1935c",
              cursor: "pointer",
            }}
            onClick={() => {
              handlePreviewGeneralTerms(bid.documentId, {
                title: "Bank Guarantee Document Preview",
              });
            }}
          />
        </div>
        {/* --- FIX: Display Actual Status Dynamically --- */}
        <div
          style={{ width: "10%" }}
          className={`${
            winnerBidStatus === "ACCEPTED" || winnerBidStatus === "ALLOTED"
              ? "accepted"
              : winnerBidStatus === "PENDING"
                ? "pending"
                : "rejected"
          }`}
        >
          {winnerBidStatus === "ABSCOND"
            ? "Rejected"
            : formatStatusText(winnerBidStatus)}
        </div>
        {/* --------------------------------------------- */}
      </div>
    </>
  );
};

// --- Sub-Component: RequisitionRow ---
const RequisitionRow = ({
  requisition,
  tenderData,
  editingState,
  setEditingState,
  onUpdateQuantity,
  onToggleStatus,
}) => {
  const {
    dateStartAt,
    dateEndAt,
    slotStartAt,
    slotEndAt,
    requiredQty,
    lowestBidPrice,
    bids,
    minimumQty,
  } = requisition;

  const filledQty = (bids || [])
    .filter((bid) => bid?.winnerBidStatus === "ACCEPTED")
    .reduce(
      (sum, acceptedBid) =>
        // Use requestedQuantity if it exists, otherwise fallback to selectedQuantity
        sum +
        (acceptedBid?.requestedQuantity || acceptedBid?.selectedQuantity || 0),
      0,
    );

  return (
    <div className={Styles.detailsMainCont}>
      <div className="display_flex_row tableDetails">
        <div style={{ width: "20%" }}>
          {formatDateElement(dateStartAt)} to {formatDateElement(dateEndAt)}
        </div>
        <div style={{ width: "20%" }}>
          {slotStartAt} to {slotEndAt === "23:59" ? "24:00" : slotEndAt}
        </div>
        <div style={{ width: "20%" }}>{requiredQty}</div>
        <div style={{ width: "10%" }}>{minimumQty}</div>
        <div style={{ width: "10%" }}>{lowestBidPrice}</div>
        <div style={{ width: "10%" }}>
          <CircularProgress
            value={filledQty}
            size={40}
            strokeWidth={4}
            required={requiredQty}
          />
        </div>
      </div>

      <div className={`${Styles.tableListBids}`}>
        <div
          className={`tableHeading_type2 display_flex_row ${Styles.bidHeading}`}
        >
          <div style={{ width: "10%" }}>Bid Price (₹/kwh)</div>
          <div style={{ width: "10%" }}>Bid Quantity(MW)</div>
          <div style={{ width: "20%" }}>Edit Allocated Quantity(MW)</div>
          <div style={{ width: "20%" }}>Allocated Quantity(MW)</div>
          <div style={{ width: "10%" }}>Bank Gurantee</div>
          <div style={{ width: "10%" }}>Status</div>
        </div>
        <div className={`scroll_bar_y ${Styles.tableDetailsCont}`}>
          {bids
            ?.slice() // Create a copy so we don't mutate the original array
            .sort((a, b) => Number(a.bidPrice) - Number(b.bidPrice)) // Sort Low -> High
            .map((bid) => (
              <BidRow
                key={bid.id}
                bid={bid}
                tenderStatus={tenderData?.tenderStatus}
                editingState={editingState}
                setEditingState={setEditingState}
                onUpdateQuantity={onUpdateQuantity}
                onToggleStatus={onToggleStatus}
                minimumQty={minimumQty}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
const RaDocumentation = ({
  open,
  handleClose,
  data,
  handleSubmit,
  tenderData,
  rfq,
}) => {
  const notify = useSelector((state) => state?.notificationSlice?.latest);

  const [confirmation, setConfirmation] = useState({
    status: false,
    header: "",
    data: "",
    note: "",
  });
  const [reqList, setReqList] = useState([]);
  const [editingState, setEditingState] = useState({
    editId: "",
    tempQuantity: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    handleSettlement();
  }, [data]);

  useEffect(() => {
    if (
      notify?.key === "BSRANotifyAmountChangeResponseConsumer" &&
      notify?.data?.tenderId === tenderData?.id
    ) {
      handleSettlement();
    }
  }, [notify]);

  const handleSettlement = async () => {
    let res = await getequisitionsList(tenderData?.id, rfq ? "CB" : "AUCTION");
    if (res?.data?.code === 200) {
      setReqList(res?.data?.data || []);
    }
  };

  const handleUpdateQuantityRequest = async (
    bidId,
    newQuantity,
    maxQuantity,
    minimumQty,
  ) => {
    // ... validation logic ...

    try {
      setIsLoading(true);
      const payload = {
        bidIdToQuantityMap: {
          [bidId]: Number(newQuantity),
        },
        status:
          Number(newQuantity) >= Number(minimumQty) ? "ACCEPTED" : "PENDING",
      };
      const res = await updateQuantityRequest(payload);

      if (res?.data?.code === 200) {
        handleSettlement();
        SuccessMessage(res?.data?.message);
        setEditingState({ editId: "", tempQuantity: "" });
        // setReqList((prevList) =>
        //   prevList.map((req) => ({
        //     ...req,
        //     bids: req.bids
        //       ? req.bids.map((bid) => {
        //           if (bid.id === bidId) {
        //             return {
        //               ...bid,
        //               selectedQuantity: Number(newQuantity), // <--- ADD THIS LINE
        //               winnerBidStatus:
        //                 Number(newQuantity) > Number(minimumQty)
        //                   ? "ACCEPTED"
        //                   : "PENDING",
        //             };
        //           }
        //           return bid;
        //         })
        //       : [],
        //   })),
        // );

        // --- FIXED STATE UPDATE BELOW ---
      }
    } catch (error) {
      console.error("Error updating quantity request:", error);
      ErrorMessage("Failed to update quantity.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (id, newQuantity) => {
    let payload = { bidIdToStatusMap: { [id]: "ACCEPTED" } };
    let res = await approveQuantityRequest(payload);
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message);
      setEditingState({ editId: "", tempQuantity: "" });
      setReqList((prevList) =>
        prevList.map((req) => ({
          ...req,
          bids: req.bids
            ? req.bids.map((bid) => {
                if (bid.id === id) {
                  return {
                    ...bid,
                    // Update both requested AND selected quantity so the calculation picks it up
                    requestedQuantity: Number(newQuantity),
                    selectedQuantity: Number(newQuantity), // <--- ADD THIS LINE
                    winnerBidStatus: "ACCEPTED",
                  };
                }
                return bid;
              })
            : [],
        })),
      );
    }
  };

  const handleStatusToggle = async (bidId, newStatus) => {
    try {
      const payload = { bidIdToStatusMap: { [bidId]: newStatus } };
      const res = await updateWinnerBidStatus(payload);
      if (res?.data?.code === 200) {
        handleSettlement();
        // SuccessMessage(res?.data?.message);

        // // Optimistic Update for Switch
        // setReqList((prevList) =>
        //   prevList.map((req) => ({
        //     ...req,
        //     bids: req.bids.map((bid) =>
        //       bid.id === bidId ? { ...bid, winnerBidStatus: newStatus } : bid,
        //     ),
        //   })),
        // );
      }
    } catch (error) {
      console.error("Error updating bid status:", error);
      ErrorMessage("Failed to update status");
    }
  };

  const handleModalSubmit = () => {
    // 1. Safety check: Ensure reqList exists before mapping
    const allBids = reqList?.flatMap((item) => item.bids || []) || [];

    // 2. Filter for Pending Bids
    const pendingBids = allBids.filter(
      (bid) =>
        (bid.bidStatus === "SHORTLISTED_RA" ||
          (bid?.bidStatus === "SHORTLISTED" && bid?.bidStage === "CB")) &&
        bid.winnerBidStatus === "PENDING",
    );

    setConfirmation({
      status: true,
      header: "Confirm Allocation",
      data: "Once confirmed, documentation will be initiated for the selected bids.",
      note: `${pendingBids?.length > 0 ? `One or more bids are pending seller approval. If you proceed with documentation, all such pending bids will be rejected.` : ""}An initial obligation will be blocked as per terms and conditions `,
    });
  };

  const handleModalFinalSubmit = () => {
    const allBids = reqList.flatMap((item) => item.bids || []);
    const approvedBids = allBids
      .filter(
        (bid) =>
          (bid.bidStatus === "SHORTLISTED_RA" ||
            (bid?.bidStatus === "SHORTLISTED" && bid?.bidStage === "CB")) &&
          bid.winnerBidStatus === "ACCEPTED",
      )
      .map((ele) => ele.id); // FIX: Removed { } to automatically return the ID
    handleSubmit(approvedBids);
  };

  return (
    <>
      {confirmation?.status && (
        <ConfirmationModal
          open={confirmation?.status}
          handleClose={() =>
            setConfirmation({
              status: false,
              header: "",
              data: "",
              note: "",
            })
          }
          data={confirmation?.data}
          header={confirmation?.header}
          note={confirmation?.note}
          handleSubmit={handleModalFinalSubmit}
        />
      )}
      <Modal open={open} onClose={handleClose} aria-labelledby="ra-doc-modal">
        <div className={`popupModal ${Styles.selectionModal}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">Agreement</div>
            <Close
              style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>

          <div className={Styles.tableContainer}>
            <div className="display_flex_row tableHeading">
              <div style={{ width: "20%" }}>Start and End Date</div>
              <div style={{ width: "20%" }}>Start and End Time</div>
              <div style={{ width: "20%" }}>Req. Quantity(MW)</div>
              <div style={{ width: "10%" }}>Min. Quantity(MW)</div>
              <div style={{ width: "10%" }}>Lowest Bid Price (₹/kwh)</div>
              <div style={{ width: "10%" }}>Bucket Filled</div>
            </div>

            <div className={`scroll_bar_y ${Styles.mainScrollElement}`}>
              {reqList.map((requisition, index) => (
                <RequisitionRow
                  key={index}
                  requisition={requisition}
                  tenderData={tenderData}
                  editingState={editingState}
                  setEditingState={setEditingState}
                  onUpdateQuantity={handleUpdateQuantityRequest}
                  onToggleStatus={handleStatusToggle}
                />
              ))}
            </div>
          </div>

          <div
            className={`activeButton ${Styles.submitButton} ${
              isLoading ? Styles.disabled : ""
            }`}
            onClick={!isLoading ? handleModalSubmit : undefined}
          >
            {isLoading ? "Processing..." : "Proceed for Documentation"}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RaDocumentation;
